import { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useSession } from '@supabase/auth-helpers-react';
import { supabase } from '../lib/supabase';
import type { CookieConsent as CookieConsentType } from '../types/database';

const COOKIE_CONSENT_KEY = 'campwhiz_cookie_consent';
const COOKIE_CONSENT_DAYS = 30; // 30 days for public/unauthenticated users

export default function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const session = useSession();

  useEffect(() => {
    // For unauthenticated users, check localStorage first
    if (!session?.user?.id) {
      const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (savedConsent) {
        try {
          const consentData = JSON.parse(savedConsent);
          const expiresAt = new Date(consentData.expiresAt);
          
          // Check if consent has expired
          if (expiresAt > new Date()) {
            // Consent is still valid, don't show banner
            setIsVisible(false);
            return;
          } else {
            // Consent expired, remove it and show banner
            localStorage.removeItem(COOKIE_CONSENT_KEY);
          }
        } catch (error) {
          console.error('Error parsing cookie consent from localStorage:', error);
          localStorage.removeItem(COOKIE_CONSENT_KEY);
        }
      }
      
      // Show banner if no valid consent found
      setIsVisible(true);
      return;
    }

    async function checkConsent() {
      try {
        if (!session?.user?.id) {
          setIsVisible(true);
          return;
        }
        
        // Check if user has localStorage consent that should be migrated to database
        const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
        if (savedConsent) {
          try {
            const consentData = JSON.parse(savedConsent);
            const expiresAt = new Date(consentData.expiresAt);
            
            // If localStorage consent is still valid, migrate it to database
            if (expiresAt > new Date()) {
              // Use the longer of: localStorage expiration or 6 months from now
              const dbExpires = new Date();
              dbExpires.setMonth(dbExpires.getMonth() + 6);
              const finalExpires = expiresAt > dbExpires ? expiresAt : dbExpires;
              
              // Migrate to database using upsert (handles both insert and update)
              const { error: upsertError } = await supabase
                .from('cookie_consents')
                .upsert({
                  user_id: session.user.id,
                  accepted: true,
                  expires_at: finalExpires.toISOString()
                }, {
                  onConflict: 'user_id',
                  ignoreDuplicates: false // Update if exists
                });
              
              if (upsertError) {
                // If upsert fails due to RLS (403), foreign key constraint, or other expected errors,
                // just keep localStorage - don't migrate and don't log as error
                if (upsertError.code === '23503' || 
                    upsertError.code === '409' || 
                    upsertError.code === 'PGRST301' ||
                    upsertError.message?.includes('403') ||
                    upsertError.message?.includes('permission denied') ||
                    upsertError.message?.includes('row-level security')) {
                  // Silently handle expected permission errors - user might not be fully authenticated yet
                  // Keep localStorage, don't show error
                } else {
                  // Only log unexpected errors
                  console.error('Error migrating consent to database:', upsertError);
                }
                // Don't clear localStorage if migration fails
              } else {
                // Successfully migrated, clear localStorage
                localStorage.removeItem(COOKIE_CONSENT_KEY);
              }
            } else {
              // Expired localStorage consent, remove it
              localStorage.removeItem(COOKIE_CONSENT_KEY);
            }
          } catch (error) {
            console.error('Error parsing localStorage consent during migration:', error);
            localStorage.removeItem(COOKIE_CONSENT_KEY);
          }
        }
        
        const { data: consents, error } = await supabase
          .from('cookie_consents')
          .select('*')
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error checking cookie consent:', error);
          // If there's an error (e.g., foreign key constraint), check localStorage as fallback
          const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
          if (savedConsent) {
            try {
              const consentData = JSON.parse(savedConsent);
              const expiresAt = new Date(consentData.expiresAt);
              if (expiresAt > new Date()) {
                console.log('[CookieConsent] Using valid localStorage consent (DB error)');
                setIsVisible(false);
                return;
              }
            } catch (e) {
              // Invalid localStorage data
            }
          }
          setIsVisible(true);
          return;
        }

        const consent = consents?.[0];

        if (consent && consent.expires_at) {
          const expiresAt = new Date(consent.expires_at);
          if (expiresAt > new Date()) {
            console.log('[CookieConsent] Valid database consent found, hiding banner');
            setIsVisible(false);
            return;
          } else {
            console.log('[CookieConsent] Database consent expired');
          }
        } else {
          // Check localStorage as fallback
          const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
          if (savedConsent) {
            try {
              const consentData = JSON.parse(savedConsent);
              const expiresAt = new Date(consentData.expiresAt);
              if (expiresAt > new Date()) {
                console.log('[CookieConsent] Using valid localStorage consent (no DB record)');
                setIsVisible(false);
                return;
              }
            } catch (e) {
              // Invalid localStorage data
            }
          }
          console.log('[CookieConsent] No valid consent found, showing banner');
        }

        setIsVisible(true);
      } catch (error) {
        console.error('Error in checkConsent:', error);
        setIsVisible(true);
      }
    }

    void checkConsent();
  }, [session?.user?.id]);

  const handleAccept = async () => {
    // For unauthenticated users, save to localStorage with expiration
    if (!session?.user?.id) {
      try {
        const expiresAt = new Date();
        expiresAt.setDate(expiresAt.getDate() + COOKIE_CONSENT_DAYS);
        
        const consentData = {
          accepted: true,
          expiresAt: expiresAt.toISOString(),
          createdAt: new Date().toISOString()
        };
        
        localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
        setIsVisible(false);
      } catch (error) {
        console.error('Error saving cookie consent to localStorage:', error);
        // Still hide the banner even if localStorage fails
        setIsVisible(false);
      }
      return;
    }

    // For authenticated users, save to database
    try {
      const expires = new Date();
      expires.setMonth(expires.getMonth() + 6);

      // Use upsert to handle both insert and update cases
      // This avoids foreign key constraint issues and handles existing records
      const { error: upsertError } = await supabase
        .from('cookie_consents')
        .upsert({
          user_id: session.user.id,
          accepted: true,
          expires_at: expires.toISOString()
        }, {
          onConflict: 'user_id',
          ignoreDuplicates: false // Update if exists
        });

      if (upsertError) {
        console.error('Error upserting consent:', upsertError);
        // If upsert fails (e.g., foreign key constraint), fall back to localStorage
        if (upsertError.code === '23503' || upsertError.code === '23505' || upsertError.code === '409') {
          console.warn('Database constraint violation, falling back to localStorage');
          const consentData = {
            accepted: true,
            expiresAt: expires.toISOString(),
            createdAt: new Date().toISOString()
          };
          localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
        }
      } else {
        console.log('[CookieConsent] Successfully saved consent to database');
        // Clear localStorage if database save succeeded
        localStorage.removeItem(COOKIE_CONSENT_KEY);
      }

      // Always hide the banner, even if there were errors
      setIsVisible(false);
    } catch (error) {
      console.error('Error in handleAccept:', error);
      // Fall back to localStorage on any error
      try {
        const expires = new Date();
        expires.setMonth(expires.getMonth() + 6);
        const consentData = {
          accepted: true,
          expiresAt: expires.toISOString(),
          createdAt: new Date().toISOString()
        };
        localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
      } catch (localStorageError) {
        console.error('Error saving to localStorage:', localStorageError);
      }
      setIsVisible(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg transform transition-transform duration-500 ease-in-out z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <p className="text-sm text-gray-700">
              We use cookies to enhance your experience. By continuing to visit this site you agree to our use of cookies.{' '}
              <a href="/privacy" className="text-primary hover:text-primary/80 underline">
                Learn more
              </a>
            </p>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            <button
              onClick={handleAccept}
              className="btn btn-primary"
            >
              Accept
            </button>
            <button
              onClick={handleAccept}
              className="text-gray-400 hover:text-gray-500"
              aria-label="Close"
              title="Accept and close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export async function resetCookieConsent(userId: string) {
  if (!userId) return;
  
  try {
    await supabase
      .from('cookie_consents')
      .delete()
      .eq('user_id', userId);
  } catch (error) {
    console.error('Error resetting cookie consent:', error);
  }
} 